/**
 * ADR 023 (BUG-865) — canonical "openable-for-bidding" predicate.
 *
 * The SINGLE source of truth for the question every open path asks: *which lots
 * should this action stamp biddable?* It is the PRE-open structural gate —
 * distinct from `deriveLotPhase`, which classifies a lot from its POST-open
 * decision stamps. A lot is openable iff it is structurally ready to receive
 * bids: marked Ready (`readied_at` stamped), has a lot number, and has a
 * scheduled close time — and carries no terminal/paused stamp.
 * (BL-774: `is_listed` was retired — a lot's Ready stamp is now the visibility
 * gate, so the openability field is the lot's own `readied_at`.)
 *
 * BL-1215: `ring_id` was REMOVED from this gate. Under PRJ-45 rings are optional
 * and ordinal, so `ring_id = NULL` is a legitimate, fully-biddable state — not an
 * incomplete one. Requiring a ring made every lot in a rings-free sale
 * permanently un-openable. Do not re-add it: `lot_number` and `close_time` are
 * the only structural fields, and the SQL mirror must move in lockstep.
 *
 * Before BUG-865 this rule was written out THREE times, drift-prone:
 *   - `stampMakeReadyLotsDecisionStamps` (SQL WHERE, admin auction-repository)
 *   - `OpenTimedAuctionService` step 4b (JS filter)
 *   - `findMissingBiddabilityFields` (per-lot open gate)
 * They now all consume this predicate. Its SQL mirror is `openableLotWhere`
 * (phase-where.ts, server-only), gated against this form by the Postgres parity
 * test `openable-where-parity.integration.test.ts` — the same anti-drift pattern
 * ADR 023 uses for `deriveLotPhase` ↔ `lotPhaseWhere` (BL-639).
 *
 * Pure / client-safe (nothing server-only transits) per ADR 010 / sharing.md §3:
 * both a raw `auction_lots` row and a projection satisfy the input by structure.
 *
 * NOTE (close_time): openability requires a NON-NULL close_time, not a *future*
 * one — it matches the make-ready cascade's historical predicate. Whether the
 * lot will *derive* `open-for-bidding` right now (which also needs a future
 * close) is `deriveLotPhase`'s concern, evaluated after the stamp lands.
 */

/** The structural fields a lot must have to be opened for bidding, as snake_case
 *  column names (used verbatim in the per-lot open `MISSING_FIELDS` error). */
export const LOT_OPENABILITY_FIELDS = ['readied', 'lot_number', 'close_time'] as const;
export type LotOpenabilityField = (typeof LOT_OPENABILITY_FIELDS)[number];

/** The structural columns the openability predicate reads.
 *
 *  BL-1215: `ringId` is **accepted but never read**. It stays on the shape for two
 *  reasons: a raw `auction_lots` row satisfies the input by structure (per the
 *  pure/client-safe contract above) without callers having to strip a column they
 *  naturally hold, and — more importantly — it keeps "a null ring does not block
 *  opening" *expressible* as an assertion. Delete it and that property becomes
 *  untestable at this layer, because the input can no longer carry a ring at all. */
export interface LotOpenabilityFieldInput {
  readiedAt: string | null;
  lotNumber: string | null;
  closeTime: string | null;
  /** Accepted and ignored — see the note above. Never gates openability. */
  ringId?: string | null;
}

/** The terminal / paused stamps that disqualify a lot from being (re)opened. */
export interface LotOpenabilityGuardInput {
  hammeredAt: string | null;
  closeProcessedAt: string | null;
  withdrawnAt: string | null;
  deletedAt: string | null;
  pausedAt: string | null;
}

/** Full input to {@link isLotOpenable} — a raw `auction_lots` row satisfies it. */
export type LotOpenableInput = LotOpenabilityFieldInput & LotOpenabilityGuardInput;

/** Options for {@link findMissingOpenabilityFields}. */
export interface OpenabilityCheckOptions {
  /**
   * The caller COMPOSES the ready stamp as part of the same write, so a missing
   * `readied_at` is not disqualifying — omit `readied` from the result.
   *
   * BL-1428 (M2, matrix §3.3/§3.4). The single-lot operator doors
   * (`LotStatusTransitionService.openForBiddingTx` / `openForPreBiddingTx`) open
   * a DRAFT lot directly: `stampLotOpened` / `stampLotPreBidding` already
   * COALESCE `readied_at` alongside their own flag in ONE UPDATE, so reporting
   * `readied` as missing refused a lot the very next statement would have
   * readied anyway.
   *
   * Opt-in per call site rather than a change to the default, because the OTHER
   * caller — the auction-open cascade (`openableLotWhere` / OpenTimedAuctionService)
   * — sweeps the ready set and must keep requiring the stamp. A draft lot is not
   * part of an auction going live.
   *
   * Narrow on purpose: it suppresses `readied` and NOTHING else. `lot_number`
   * and `close_time` have no composite that supplies them, so they are still
   * reported (BUG-1191's gate ordering is unchanged).
   */
  readonly composeReady?: boolean;
}

/**
 * The structural fields this lot is missing to be openable, in
 * {@link LOT_OPENABILITY_FIELDS} order. Empty ⇒ all present.
 *
 * @param lot  The lot's structural columns.
 * @param opts See {@link OpenabilityCheckOptions}.
 * @sideEffect none — pure.
 */
export function findMissingOpenabilityFields(
  lot: LotOpenabilityFieldInput,
  opts?: OpenabilityCheckOptions,
): LotOpenabilityField[] {
  const missing: LotOpenabilityField[] = [];
  if (!lot.readiedAt && !opts?.composeReady) missing.push('readied');
  if (lot.lotNumber == null) missing.push('lot_number');
  // BL-1215: no ring check — a ringless lot is fully openable.
  if (!lot.closeTime) missing.push('close_time');
  return missing;
}

/** True when the lot has every structural openability field.
 *  @param opts See {@link OpenabilityCheckOptions}.
 *  @sideEffect none — pure. */
export function hasOpenabilityFields(
  lot: LotOpenabilityFieldInput,
  opts?: OpenabilityCheckOptions,
): boolean {
  return findMissingOpenabilityFields(lot, opts).length === 0;
}

/** True when a terminal or paused stamp blocks (re)opening.
 *  @sideEffect none — pure. */
export function isLotBlockedFromOpening(lot: LotOpenabilityGuardInput): boolean {
  return (
    lot.hammeredAt != null ||
    lot.closeProcessedAt != null ||
    lot.withdrawnAt != null ||
    lot.deletedAt != null ||
    lot.pausedAt != null
  );
}

/**
 * Canonical predicate: is this lot structurally ready to be opened for bidding?
 * All openability fields present AND no terminal/paused stamp.
 *
 * **The default is the JS mirror of `openableLotWhere` (phase-where.ts)** and the
 * parity test pins them together — call it with NO options anywhere that mirror
 * matters (the auction-open cascade's lot sweep).
 *
 * `{ composeReady: true }` deliberately steps outside that mirror for the
 * per-lot operator doors, which compose the ready stamp into the same UPDATE
 * (BL-1428 M2). The check API passes it so its verdict agrees with the write it
 * gates — a check that said `not-openable` where `openForBiddingTx` succeeds
 * would grey a dropdown row the server would have accepted.
 *
 * @param opts See {@link OpenabilityCheckOptions}.
 * @sideEffect none — pure.
 */
export function isLotOpenable(lot: LotOpenableInput, opts?: OpenabilityCheckOptions): boolean {
  return hasOpenabilityFields(lot, opts) && !isLotBlockedFromOpening(lot);
}

/** Human-readable label for each openability field, for operator-facing
 *  "these lots can't open" messages.
 *
 *  BL-1129: `readied` reads "not marked Upcoming" — the KEY is the ADR-023
 *  stamp name (`readied_at`), the VALUE is operator copy, and the operator's
 *  word for that lot phase is now "Upcoming" everywhere else in the app. */
export const LOT_OPENABILITY_FIELD_LABELS: Record<LotOpenabilityField, string> = {
  readied: 'not marked Upcoming',
  lot_number: 'lot number',
  close_time: 'close time',
};
